import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { serverLocation } from 'src/app/globals';
import { TokenService } from '../../shared/services/token.service';
import { Product, ProductPicture } from '../classes';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  private allProductsLoading$: Observable<Product[]> = null;
  private _products: { [key: number]: ReplaySubject<Product> } = {};

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService
  ) { }

  createProduct(product: Product, note: string = null): Observable<Product> {
    return this._httpClient.post(`${serverLocation}/api/product`, {
      content: product,
      note: note
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Product>;
  }

  createProductPicture(productPicture: ProductPicture): Observable<ProductPicture> {
    return this._httpClient.post(`${serverLocation}/api/product-picture`, productPicture, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ProductPicture>;
  }

  deleteProduct(identifier: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/product/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }
  
  deleteProductPicture(identifier: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/product-picture/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  getAllProductPictures(productId: number): Observable<ProductPicture[]> {
    return this._httpClient.get(`${serverLocation}/api/product/${productId}/pictures`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ProductPicture[]>;
  }

  getAllProducts(soft: boolean = true): Observable<Product[]> {
    if (this.allProductsLoading$ === null || !soft) {
      let subject = new Subject<Product[]>();
      this.allProductsLoading$ = subject.asObservable();
      this._httpClient.get(`${serverLocation}/api/product`, {
        headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
      }).pipe(tap((products: Product[]) => {
        for (let product of products) {
          if(!this._products[product.productid]) this._products[product.productid] = new ReplaySubject<Product>(1);
          this._products[product.productid].next(product);
        }
      })).subscribe(subject);
      return subject.asObservable();
    }
    return combineLatest(Object.values(this._products)).pipe(take(1));
  }

  getProductById(identifier: number, soft: boolean = true): Observable<Product> {
    if (typeof identifier !== 'number') return throwError('no valid product identifier passed');
    if (!soft || !this._products[identifier]) {
      if (!this._products[identifier]) this._products[identifier] = new ReplaySubject(1);
      this._getProductById(identifier).subscribe({
        next: (product: Product) => this._products[identifier].next(product),
        error: (error: HttpErrorResponse) => this._products[identifier].error(error)
      });
    }
    return this._products[identifier].asObservable();
  }

  updateProduct(product: Product, note: string = null): Observable<Product> {
    return this._httpClient.put(`${serverLocation}/api/product`, {
      content: product,
      note: note
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Product>;
  }

  private _getProductById(identifier: number): Observable<Product> {
    return this._httpClient.get(`${serverLocation}/api/product/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Product>;
  }
}
